/* eslint-disable max-lines-per-function */
/* eslint-disable react/jsx-one-expression-per-line */
/*  eslint-disable-next-line functional/immutable-data */
import React, { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import {
  Badge,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useHookstate } from '@hookstate/core'
import { useLang } from '~/hooks/useLang'
import dayjs from 'dayjs'
import { authFetch, mountUrlRequest } from '~/services/fetch'
import { Controlled as ControlledZoom } from 'react-medium-image-zoom'
import 'react-medium-image-zoom/dist/styles.css'
import {
  IInspection,
  IInspectionItemRecord,
  MediaEntry,
  ViewMoreProps,
} from '~/features/Resources/fleetManagement/fleetInspection/survey/interfaces/IInspection'
export type { ViewMoreProps } from '~/features/Resources/fleetManagement/fleetInspection/survey/interfaces/IInspection'

// — Seção Inoperante: carrossel + tabela sincronizados (1 linha) + lightbox —
function InoperanteSection({
  records,
  translate,
  onZoomChange,
}: {
  readonly records: readonly IInspectionItemRecord[]
  readonly translate: (key: string) => string
  readonly onZoomChange: (zoomed: boolean) => void
}) {
  const mediaEntries: readonly MediaEntry[] = records.flatMap((rec) => {
    if (rec.medias && rec.medias.length > 0) {
      return rec.medias.map((media) => ({
        path: media.media_path,
        recordId: rec.id,
      }))
    }
    if (rec.media_path) return [{ path: rec.media_path, recordId: rec.id }]
    return []
  })

  const [mediaIndex, setMediaIndex] = useState(0)
  const [isZoomed, setIsZoomed] = useState(false)

  const handleZoomChange = (zoomed: boolean) => {
    setIsZoomed(zoomed)
    onZoomChange(zoomed)
  }

  const activeEntry = mediaEntries[mediaIndex] ?? null
  const activeRecord = activeEntry
    ? records.find((rec) => rec.id === activeEntry.recordId) ?? null
    : null
  const hasMedia = mediaEntries.length > 0

  // Registros exibidos na tabela: somente o ativo quando há mídia, todos caso contrário
  const visibleRecords: readonly IInspectionItemRecord[] = hasMedia
    ? activeRecord
      ? [activeRecord]
      : []
    : records

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Inoperante
        </Typography>
        <Badge
          badgeContent={records.length}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              position: 'relative',
              transform: 'none',
              ml: 0.5,
            },
          }}
        />
      </Box>

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                }}
              >
                <TableCell
                  sx={{
                    width: '30%',
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.8125rem',
                  }}
                >
                  Nome
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    width: '30%',
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.8125rem',
                  }}
                >
                  Item obrigatório de vistoria?
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    width: '40%',
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.8125rem',
                  }}
                >
                  Justificativa
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRecords.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    align="center"
                    sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                  >
                    Nenhum item a ser exibido
                  </TableCell>
                </TableRow>
              ) : (
                visibleRecords.map((rec) => (
                  <TableRow key={rec.id}>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{rec.inspection_item?.name ?? '-'}</TableCell>
                    <TableCell align="center" sx={{ wordBreak: 'break-word' }}>
                      {rec.inspection_item?.required == null
                        ? '-'
                        : rec.inspection_item?.required
                        ? translate('Yes')
                        : translate('No')}
                    </TableCell>
                    <TableCell align="center" sx={{ wordBreak: 'break-word' }}>{rec.notes || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {hasMedia && activeEntry && (
          <Box sx={{ position: 'relative', textAlign: 'center', mt: 2, pb: 2 }}>
            {mediaEntries.length > 1 && (
              <IconButton
                onClick={() =>
                  setMediaIndex(
                    (idx) =>
                      (idx - 1 + mediaEntries.length) % mediaEntries.length
                  )
                }
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  bgcolor: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                }}
                size="small"
              >
                <ChevronLeftIcon />
              </IconButton>
            )}

            <ControlledZoom
              isZoomed={isZoomed}
              onZoomChange={handleZoomChange}
              zoomMargin={50}
            >
              <img
                src={mountUrlRequest(
                  `/inspection/get-media/${activeEntry.path}`
                )}
                alt="Anexo"
                style={{
                  maxHeight: 220,
                  maxWidth: '100%',
                  borderRadius: 4,
                  objectFit: 'contain',
                }}
              />
            </ControlledZoom>

            {mediaEntries.length > 1 && (
              <IconButton
                onClick={() =>
                  setMediaIndex((idx) => (idx + 1) % mediaEntries.length)
                }
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  bgcolor: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                }}
                size="small"
              >
                <ChevronRightIcon />
              </IconButton>
            )}

            {mediaEntries.length > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 0.5,
                  mt: 1,
                }}
              >
                {mediaEntries.map((entry, dotIndex) => (
                  <Box
                    key={`${entry.recordId}-${entry.path}`}
                    onClick={() => setMediaIndex(dotIndex)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      bgcolor:
                        dotIndex === mediaIndex ? 'primary.main' : 'grey.400',
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  )
}

// — Tabela de uma seção de status —
function SectionTable({
  title,
  badgeColor,
  records,
  translate,
}: {
  readonly title: string
  readonly badgeColor: 'success' | 'error' | 'default' | 'warning' | 'info'
  readonly records: readonly IInspectionItemRecord[]
  readonly translate: (key: string) => string
}) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        <Badge
          badgeContent={records.length}
          color={badgeColor}
          sx={{
            '& .MuiBadge-badge': {
              position: 'relative',
              transform: 'none',
              ml: 0.5,
            },
          }}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
              }}
            >
              <TableCell
                sx={{
                  width: '30%',
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '0.8125rem',
                }}
              >
                Nome
              </TableCell>
              {/* Coluna pendente de disponibilização pelo backend (campo de obrigatoriedade) */}
              <TableCell
                align="center"
                sx={{
                  width: '30%',
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '0.8125rem',
                }}
              >
                Item obrigatório de vistoria?
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  width: '40%',
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '0.8125rem',
                }}
              >
                Justificativa
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                >
                  Nenhum item a ser exibido
                </TableCell>
              </TableRow>
            ) : (
              records.map((rec) => (
                <TableRow key={rec.id}>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{rec.inspection_item?.name ?? '-'}</TableCell>
                  <TableCell align="center" sx={{ wordBreak: 'break-word' }}>
                    {rec.inspection_item?.required == null
                      ? '-'
                      : rec.inspection_item?.required
                      ? translate('Yes')
                      : translate('No')}
                  </TableCell>
                  <TableCell align="center" sx={{ wordBreak: 'break-word' }}>{rec.notes || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default function ViewMore({
  open,
  warehouseId: inspectionId,
  handleClose,
}: ViewMoreProps) {
  const { translate } = useLang()
  const inspection = useHookstate<IInspection | null>(null)
  const loading = useHookstate(false)
  const isZoomedRef = React.useRef(false)
  const suppressCloseRef = React.useRef(false)

  React.useEffect(() => {
    const handleGlobalMouseDown = (event: MouseEvent) => {
      const target = event.target as Element
      if (
        target.closest('[data-rmiz-modal]') ||
        target.closest('[data-rmiz-modal-overlay]') ||
        target.closest('[data-rmiz-btn-unzoom]')
      ) {
        suppressCloseRef.current = true

        setTimeout(() => {
          suppressCloseRef.current = false
        }, 600)
      }
    }
    document.addEventListener('mousedown', handleGlobalMouseDown, true)
    return () =>
      document.removeEventListener('mousedown', handleGlobalMouseDown, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!inspectionId) return
    loading.set(true)
    authFetch({ url: `/inspection/${inspectionId}`, method: 'GET' })
      .then((res) => inspection.set(res.data))
      .finally(() => loading.set(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionId])

  const data = inspection.get()
  const records: readonly IInspectionItemRecord[] =
    data?.inspection_item_records ?? []

  const conformidade = records.filter((rec) => rec.item_status === 1)
  const inconformidade = records.filter((rec) => rec.item_status === 2)
  const naoVistoriado = records.filter((rec) => rec.item_status === 3)
  const inoperante = records.filter((rec) => rec.item_status === 4)

  const clickOutModalClose = (
    _event: unknown,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (isZoomedRef.current || suppressCloseRef.current) return
    if (reason !== 'backdropClick') open.set(false)
  }

  const safeHandleClose = () => {
    if (isZoomedRef.current || suppressCloseRef.current) return
    handleClose()
  }

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open.get()}
      onClose={clickOutModalClose}
      disableEscapeKeyDown={isZoomedRef.current}
    >
      <DialogTitle
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        {translate('Inspection')}
        <IconButton aria-label="close" onClick={safeHandleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading.get() ? (
          <Grid
            item
            xs={12}
            display="flex"
            sx={{ justifyContent: 'center', padding: 4 }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <>
            {/* Cabeçalho */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={2.4}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Dispositivo:
                </Typography>
                <Typography variant="body2">
                  {data?.device?.name ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2.4}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Aprovada:
                </Typography>
                <Typography variant="body2">
                  {data?.is_operational == null
                    ? '-'
                    : data.is_operational
                    ? translate('Yes')
                    : translate('No')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2.4}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Agência:
                </Typography>
                <Typography variant="body2">
                  {data?.agency?.name ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2.4}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Grupo de Despacho:
                </Typography>
                <Typography variant="body2">
                  {data?.dispatch_group?.name ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2.4}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Data de Vistoria:
                </Typography>
                <Typography variant="body2">
                  {data?.created_at
                    ? dayjs(data.created_at).format('DD/MM/YYYY HH:mm:ss')
                    : '-'}
                </Typography>
              </Grid>
            </Grid>

            {/* Seções por status */}
            <SectionTable
              title="Em conformidade"
              badgeColor="success"
              records={conformidade}
              translate={translate}
            />
            <SectionTable
              title="Inconformidade"
              badgeColor="warning"
              records={inconformidade}
              translate={translate}
            />
            <SectionTable
              title="Não vistoriado"
              badgeColor="info"
              records={naoVistoriado}
              translate={translate}
            />
            <InoperanteSection
              records={inoperante}
              translate={translate}
              onZoomChange={(zoomed) => {
                isZoomedRef.current = zoomed
              }}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
